
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { motion } from 'framer-motion';
import { Building, Mail, Lock, User, Briefcase, Hotel, Home, CheckSquare } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { USER_ROLE } from '@/types/user-role';

const SignupPage = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState(USER_ROLE.TECHNICIAN);
  const [enableStationaryEngineer, setEnableStationaryEngineer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || !password || !fullName || !role) {
      toast({
        title: "Missing Fields",
        description: "Please fill out all fields to create an account.",
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await signUp(email, password, {
        full_name: fullName,
        role: role,
        enable_stationary_engineer: enableStationaryEngineer,
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Account Created!",
        description: "Please check your email to verify your account before logging in.",
        variant: "success",
      });
      navigate('/login');
    } catch (error) {
      toast({
        title: "Signup Failed",
        description: error.message || "Could not create your account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <motion.div
        initial={{ opacity: 0, y: -50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md p-8 space-y-6 bg-slate-900/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-purple-500/20"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: 360 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 20 }}
          >
            <Building className="mx-auto h-12 w-12 text-purple-400" />
          </motion.div>
          <h1 className="text-3xl font-bold text-white mt-4">Create Your Account</h1>
          <p className="text-gray-400">Join the platform and streamline your workflow.</p>
        </div>
        <form onSubmit={handleSignup} className="space-y-6">
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              id="full-name"
              type="text"
              placeholder="Full Name"
              className="pl-10 bg-slate-800/60 border-slate-700 text-white focus:ring-purple-500 focus:border-purple-500"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              id="email"
              type="email"
              placeholder="Email"
              className="pl-10 bg-slate-800/60 border-slate-700 text-white focus:ring-purple-500 focus:border-purple-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              id="password"
              type="password"
              placeholder="Password"
              className="pl-10 bg-slate-800/60 border-slate-700 text-white focus:ring-purple-500 focus:border-purple-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="role" className="text-gray-400 mb-2 block">Select Your Role</Label>
            <Select onValueChange={setRole} defaultValue={role}>
              <SelectTrigger className="w-full bg-slate-800/60 border-slate-700 text-white focus:ring-purple-500 focus:border-purple-500">
                <Briefcase className="inline-block mr-2 h-5 w-5 text-gray-400" />
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-white">
                <SelectItem value={USER_ROLE.TECHNICIAN}>Technician</SelectItem>
                <SelectItem value={USER_ROLE.SUPERVISOR}>Supervisor</SelectItem>
                <SelectItem value={USER_ROLE.OWNER}>Owner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Stationary Engineer Feature Toggle */}
          <div className="p-4 bg-slate-800/30 rounded-lg border border-purple-500/20">
            <div className="flex items-center gap-3 mb-3">
              <Hotel className="w-5 h-5 text-purple-400" />
              <Label className="text-gray-300 font-medium">Stationary Engineer Features</Label>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-400 mb-2">
                  Enable advanced features for hotel and multi-unit apartment maintenance
                </p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <CheckSquare className="w-3 h-3" />
                    PM (Preventive Maintenance)
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckSquare className="w-3 h-3" />
                    RMI (Room Maintenance Inspection)
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckSquare className="w-3 h-3" />
                    GRM (Guest Room Maintenance)
                  </span>
                </div>
              </div>
              <Button
                type="button"
                variant={enableStationaryEngineer ? "default" : "outline"}
                size="sm"
                onClick={() => setEnableStationaryEngineer(!enableStationaryEngineer)}
                className={enableStationaryEngineer ? "bg-purple-600 hover:bg-purple-700" : "border-purple-500 text-purple-400 hover:bg-purple-500/20"}
              >
                {enableStationaryEngineer ? "Enabled" : "Enable"}
              </Button>
            </div>
          </div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg" disabled={isSubmitting}>
              {isSubmitting ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </motion.div>
        </form>
        <p className="text-center text-sm text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-purple-400 hover:text-purple-300">
            Log In
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default SignupPage;
